// REL_1.1.15 F-262 §2 — Currency selector (segmented control).
// Per Corrigendum 2: country is hardcoded to "US"; this control lets visitors manually override
// the currency in which prices display.
import { useCurrencySettings } from "@/hooks/useCurrencySettings";
import { CURRENCIES, type CurrencyCode } from "@/lib/currency";

interface Props {
  className?: string;
  label?: string;
}

export default function CurrencySelector({ className, label = "Currency:" }: Props) {
  const { currency, setCurrency } = useCurrencySettings();

  // REL_1.1.15E F-313 (S-1228): with CAD hidden (US-only) there's a single currency → a one-option
  // segmented control is pointless. Self-hide when there's nothing to choose. Reversible: re-adding a
  // currency in lib/currency.ts brings the selector back automatically.
  if (CURRENCIES.length <= 1) return null;

  return (
    <div className={`inline-flex items-center gap-3 text-sm ${className ?? ""}`}>
      <span className="text-gray-600">{label}</span>
      <div
        className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white"
        role="radiogroup"
        aria-label="Select display currency"
      >
        {CURRENCIES.map((c) => {
          const isActive = c.code === currency;
          return (
            <button
              key={c.code}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={`Display prices in ${c.label}`}
              onClick={() => setCurrency(c.code as CurrencyCode)}
              className={
                isActive
                  ? "bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-semibold text-white"
                  : "bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
              }
            >
              {c.code}
            </button>
          );
        })}
      </div>
    </div>
  );
}
